import { useEffect, useRef, useState } from "react"
import { Box } from "@mui/material"
import { HasInvalidated } from "event-definitions"
import { Bound } from "lib/@components/binding/Bound"
import { Loader } from "lib/@components/Loader"
import { NotOnMobile } from "lib/@components/mobile"
import { If } from "lib/@components/switch"
import { TerminatedError } from "lib/@hooks/useAsync"
import { AuditLog } from "./audit-log"
import { AuditTopBar } from "./top-bar"
import { getAuditsByReferenceIdAndType } from "../controller/get-audits-by-reference-id"

const QUERY_NAME = "getAuditsByReferenceIdAndType"

/**
 * Activity log that fetches audits a page at a time and loads the next page as the user scrolls.
 * Used for activity logs that can hold tens of thousands of entries (e.g. a regime promoted from a
 * scenario), where fetching and rendering everything at once hangs the drawer.
 *
 * Mirrors what useResults does for the non-paginated container: reads go through the normal Apollo
 * cache, and the log reloads when audits are invalidated (`invalidate()` evicts the cache, so the
 * reload is fresh). `fetchAll` is provided via Bound so the Download button can export the full log.
 */
export function PaginatedAuditContainer({ setOpen, type, id, fileNamePrefix, filterFn, isDialog, filter, pageSize }) {
    const [items, setItems] = useState(null)
    const [loadingMore, setLoadingMore] = useState(false)
    const skipRef = useRef(0)
    const doneRef = useRef(false)
    const loadingRef = useRef(false)
    const sentinelRef = useRef(null)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(reload, [id, type])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(observeSentinel, [items, isDialog])
    // Reload from the top when audits change (e.g. tailoring added/removed), like useResults does.
    HasInvalidated(QUERY_NAME).after.useEvent(reload, [id, type])

    // The dialog instance only renders the toolbar (Exit / Download). It still needs fetchAll for
    // the export, but does not load or render any pages itself.
    if (isDialog) {
        return (
            <Bound data={items ?? []} fetchAll={fetchAll} auditId={id} type={type}>
                <AuditTopBar setOpen={setOpen} type={type} fileNamePrefix={fileNamePrefix} filterFn={filterFn} />
            </Bound>
        )
    }

    return (
        <Bound data={items ?? []} loadingData={items === null} auditId={id} type={type} fetchAll={fetchAll}>
            <NotOnMobile>
                <AuditTopBar setOpen={setOpen} type={type} fileNamePrefix={fileNamePrefix} filterFn={filterFn} />
            </NotOnMobile>
            <AuditLog setOpen={setOpen} />
            <Box ref={sentinelRef} sx={{ height: 1 }} />
            <If value={loadingMore}>
                <Box sx={{ position: "relative", height: 48 }}>
                    <Loader inline color="primary" />
                </Box>
            </If>
        </Bound>
    )

    function reload() {
        skipRef.current = 0
        doneRef.current = false
        loadingRef.current = false
        setItems(null)
        if (id && !isDialog) loadPage(true)
        return undefined
    }

    function observeSentinel() {
        const sentinel = sentinelRef.current
        if (!sentinel || isDialog) return undefined
        const root = getScrollParent(sentinel)
        const observer = new IntersectionObserver(onIntersect, { root, rootMargin: "300px" })
        observer.observe(sentinel)
        return () => observer.disconnect()
    }

    function onIntersect(entries) {
        if (entries.some((entry) => entry.isIntersecting)) loadPage(false)
    }

    async function loadPage(isReset) {
        if (loadingRef.current || doneRef.current || !id) return
        loadingRef.current = true
        setLoadingMore(true)
        const startedSkip = skipRef.current
        try {
            const page = (await getAuditsByReferenceIdAndType(id, type, filter, startedSkip, pageSize)) ?? []
            // Guard against a reload having happened while this page was in flight.
            if (skipRef.current !== startedSkip) return
            skipRef.current += page.length
            if (page.length < pageSize) doneRef.current = true
            setItems((previous) => [...(isReset ? [] : previous ?? []), ...page])
        } catch (e) {
            if (e instanceof TerminatedError) return
            console.error("[activity-log] failed to load page", e)
            // Surface an empty (not stuck) state on the first page so the log shows "no results"
            // rather than an infinite spinner.
            if (isReset) setItems((previous) => previous ?? [])
        } finally {
            loadingRef.current = false
            setLoadingMore(false)
        }
    }

    async function fetchAll() {
        try {
            return (await getAuditsByReferenceIdAndType(id, type, filter)) ?? []
        } catch (e) {
            if (!(e instanceof TerminatedError)) console.error("[activity-log] failed to fetch all", e)
            return items ?? []
        }
    }
}

function getScrollParent(node) {
    let element = node?.parentElement
    while (element) {
        const { overflowY } = getComputedStyle(element)
        if (overflowY === "auto" || overflowY === "scroll") return element
        element = element.parentElement
    }
    return null
}
